/**
 * Response Transformation Layer
 * 
 * This module handles the transformation of backend API responses (snake_case)
 * to frontend-expected formats (camelCase).
 * 
 * GAP-TYPES-001: Snake_case vs camelCase inconsistency
 * GAP-TYPES-002: Field naming inconsistencies
 * 
 * @see doc/code-review/gap-analysis/ui-backend-integration-gaps.md
 */

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Generic key transformer function type
 */
type KeyTransformer = (key: string) => string;

/**
 * Generic object transformer function type
 */
type ObjectTransformer = <T extends Record<string, unknown>>(obj: T) => T;

// =============================================================================
// Key Transformation Utilities
// =============================================================================

/**
 * Convert a snake_case string to camelCase
 * 
 * @example
 * snakeToCamel('position_id') // => 'positionId'
 * snakeToCamel('opened_at') // => 'openedAt'
 * snakeToCamel('portfolio_value_usd') // => 'portfolioValueUsd'
 */
export function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/**
 * Convert a camelCase string to snake_case
 * 
 * @example
 * camelToSnake('positionId') // => 'position_id'
 * camelToSnake('openedAt') // => 'opened_at'
 */
export function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Recursively transform all keys in an object from snake_case to camelCase
 * 
 * @param obj - The object to transform
 * @returns A new object with all keys transformed to camelCase
 * 
 * @example
 * transformKeys({ position_id: '123', opened_at: '2024-01-01' })
 * // => { positionId: '123', openedAt: '2024-01-01' }
 */
export function transformKeys<T extends Record<string, unknown>>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => 
      typeof item === 'object' && item !== null 
        ? transformKeys(item as Record<string, unknown>)
        : item
    ) as unknown as T;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  const transformed: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const newKey = snakeToCamel(key);
    transformed[newKey] = typeof value === 'object' && value !== null
      ? transformKeys(value as Record<string, unknown>)
      : value;
  }

  return transformed as T;
}

// =============================================================================
// Specific Response Transformers
// =============================================================================

/**
 * Raw backend position response (snake_case)
 */
export interface RawApiPosition {
  position_id: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entry_price: number;
  current_price: number;
  market_value: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  realized_pnl?: number;
  opened_at: string;
  updated_at?: string;
  venue?: string;
  strategy_type?: string;
  signal_type?: string;
  signal_confidence?: number;
  entry_intent_id?: string;
  entry_fill_ids?: string[];
  exit_plan?: Record<string, unknown>;
  status?: string;
  quality_tier?: string;
  holding_period_hours?: number;
}

/**
 * Transformed frontend position (camelCase)
 */
export interface TransformedPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entryPrice: number;
  currentPrice: number;
  marketValue: number;
  unrealizedPnL: number;
  unrealizedPnLPercent: number;
  realizedPnL: number;
  openedAt: string;
  updatedAt: string;
  venue: string;
  strategyType: string;
  signalType: string;
  signalConfidence: number;
  entryIntentId: string;
  entryFillIds: string[];
  exitPlan: Record<string, unknown>;
  status: string;
  qualityTier: string;
  holdingPeriodHours: number;
}

/**
 * Transform a position response from backend format to frontend format
 * 
 * GAP-TYPES-002: Field naming inconsistencies
 * - position_id → id
 * - opened_at → openedAt
 * - unrealized_pnl → unrealizedPnL
 * - unrealized_pnl_pct → unrealizedPnLPercent
 */
export function transformPosition(raw: RawApiPosition): TransformedPosition {
  return {
    id: raw.position_id,
    symbol: raw.symbol,
    side: raw.side,
    size: raw.size,
    entryPrice: raw.entry_price,
    currentPrice: raw.current_price,
    marketValue: raw.market_value,
    unrealizedPnL: raw.unrealized_pnl,
    unrealizedPnLPercent: raw.unrealized_pnl_pct,
    realizedPnL: raw.realized_pnl ?? 0,
    openedAt: raw.opened_at,
    updatedAt: raw.updated_at ?? raw.opened_at,
    venue: raw.venue ?? 'unknown',
    strategyType: raw.strategy_type ?? 'directional',
    signalType: raw.signal_type ?? '',
    signalConfidence: raw.signal_confidence ?? 0.5,
    entryIntentId: raw.entry_intent_id ?? '',
    entryFillIds: raw.entry_fill_ids ?? [],
    exitPlan: raw.exit_plan ?? {},
    status: raw.status ?? 'open',
    qualityTier: raw.quality_tier ?? 'medium',
    holdingPeriodHours: raw.holding_period_hours ?? 0,
  };
}

/**
 * Transform an array of positions
 */
export function transformPositions(positions: RawApiPosition[]): TransformedPosition[] {
  return positions.map(transformPosition);
}

/**
 * Raw backend order/intent response (snake_case)
 */
export interface RawApiOrder {
  intent_id: string;
  symbol: string;
  side: string;
  order_type?: string;
  size: number;
  price?: number;
  state: string;
  created_at: string;
  updated_at?: string;
  expires_at?: string;
  projected_pnl?: number;
  risk_score?: number;
  warnings?: string[];
  rationale?: string;
}

/**
 * Transformed frontend order (camelCase)
 */
export interface TransformedOrder {
  id: string;
  symbol: string;
  side: string;
  type: string;
  quantity: number;
  price: number | undefined;
  status: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  projectedPnL: number;
  riskScore: number;
  warnings: string[];
  rationale: string;
}

/**
 * Transform an order/intent response from backend format to frontend format
 * 
 * GAP-TYPES-002: Field naming inconsistencies
 * - intent_id → id
 * - state → status (different terminology)
 * - order_type → type
 * - created_at → createdAt
 */
export function transformOrder(raw: RawApiOrder): TransformedOrder {
  return {
    id: raw.intent_id,
    symbol: raw.symbol,
    side: raw.side,
    type: raw.order_type ?? 'market',
    quantity: raw.size,
    price: raw.price,
    status: raw.state.toLowerCase(),
    createdAt: raw.created_at,
    updatedAt: raw.updated_at ?? raw.created_at,
    expiresAt: raw.expires_at ?? '',
    projectedPnL: raw.projected_pnl ?? 0,
    riskScore: raw.risk_score ?? 0.5,
    warnings: raw.warnings ?? [],
    rationale: raw.rationale ?? '',
  };
}

/**
 * Transform an array of orders
 */
export function transformOrders(orders: RawApiOrder[]): TransformedOrder[] {
  return orders.map(transformOrder);
}

/**
 * Raw backend risk metrics response (snake_case)
 */
export interface RawApiRiskMetrics {
  portfolio_value_usd?: number;
  gross_exposure_pct?: number;
  net_exposure_pct?: number;
  drawdown_pct?: number;
  max_drawdown_pct?: number;
  utilization?: {
    gross?: number;
    net?: number;
  };
  limits?: {
    max_gross_exposure_pct?: number;
    max_net_exposure_pct?: number;
    max_position_pct?: number;
    max_drawdown_pct?: number;
  };
}

/**
 * Transformed frontend risk metrics (camelCase)
 */
export interface TransformedRiskMetrics {
  portfolioValue: number;
  grossExposurePercent: number;
  netExposurePercent: number;
  drawdownPercent: number;
  maxDrawdownPercent: number;
  utilization: {
    gross: number;
    net: number;
  };
  limits: {
    maxGrossExposurePercent: number;
    maxNetExposurePercent: number;
    maxPositionPercent: number;
    maxDrawdownPercent: number;
  };
}

/**
 * Transform risk metrics response from backend format to frontend format
 * 
 * GAP-TYPES-002: Field naming inconsistencies
 * - portfolio_value_usd → portfolioValue
 * - gross_exposure_pct → grossExposurePercent
 */
export function transformRiskMetrics(raw: RawApiRiskMetrics): TransformedRiskMetrics {
  return {
    portfolioValue: raw.portfolio_value_usd ?? 0,
    grossExposurePercent: raw.gross_exposure_pct ?? 0,
    netExposurePercent: raw.net_exposure_pct ?? 0,
    drawdownPercent: raw.drawdown_pct ?? 0,
    maxDrawdownPercent: raw.max_drawdown_pct ?? 0.1,
    utilization: {
      gross: raw.utilization?.gross ?? 0,
      net: raw.utilization?.net ?? 0,
    },
    limits: {
      maxGrossExposurePercent: raw.limits?.max_gross_exposure_pct ?? 0.8,
      maxNetExposurePercent: raw.limits?.max_net_exposure_pct ?? 0.5,
      maxPositionPercent: raw.limits?.max_position_pct ?? 0.1,
      maxDrawdownPercent: raw.limits?.max_drawdown_pct ?? 0.1,
    },
  };
}

/**
 * Raw backend system status response (snake_case)
 */
export interface RawApiSystemStatus {
  service: string;
  kill_switch: 'on' | 'off';
  pending_intents: number;
  open_positions: number;
  orders_submitted: number;
  fills_processed: number;
  system?: {
    profile: string;
    uptime_seconds: number;
    mode: string;
  };
  venues?: Array<{
    name: string;
    status: string;
    last_check: string;
  }>;
  regime?: string;
  regime_label?: string;
  vpin?: {
    level: string;
    z_score: number;
  };
}

/**
 * Transformed frontend system status (camelCase)
 */
export interface TransformedSystemStatus {
  service: string;
  killSwitchActive: boolean;
  pendingIntents: number;
  openPositions: number;
  ordersSubmitted: number;
  fillsProcessed: number;
  system: {
    profile: string;
    uptimeSeconds: number;
    mode: string;
  };
  venues: Array<{
    name: string;
    status: string;
    lastCheck: string;
  }>;
  regime: string;
  regimeLabel: string;
  vpin: {
    level: string;
    zScore: number;
  };
  overall: 'healthy' | 'degraded' | 'down';
}

/**
 * Transform system status response from backend format to frontend format
 * 
 * GAP-TYPES-002: Field naming inconsistencies
 * - kill_switch → killSwitchActive (with boolean conversion)
 * - pending_intents → pendingIntents
 * - regime_label → regimeLabel
 * - z_score → zScore
 * 
 * Also maps the overall status based on kill switch and other factors.
 */
export function transformSystemStatus(raw: RawApiSystemStatus): TransformedSystemStatus {
  // Determine overall status
  let overall: 'healthy' | 'degraded' | 'down' = 'healthy';
  
  if (raw.kill_switch === 'on') {
    overall = 'degraded';
  }
  
  // Check venue status
  if (raw.venues) {
    const allVenuesDown = raw.venues.every(v => v.status === 'offline');
    if (allVenuesDown) {
      overall = 'down';
    } else if (raw.venues.some(v => v.status === 'degraded' || v.status === 'offline')) {
      overall = 'degraded';
    }
  }

  return {
    service: raw.service,
    killSwitchActive: raw.kill_switch === 'on',
    pendingIntents: raw.pending_intents,
    openPositions: raw.open_positions,
    ordersSubmitted: raw.orders_submitted,
    fillsProcessed: raw.fills_processed,
    system: {
      profile: raw.system?.profile ?? 'balanced',
      uptimeSeconds: raw.system?.uptime_seconds ?? 0,
      mode: raw.system?.mode ?? 'notify_only',
    },
    venues: (raw.venues ?? []).map(v => ({
      name: v.name,
      status: v.status,
      lastCheck: v.last_check,
    })),
    regime: raw.regime ?? 'unknown',
    regimeLabel: raw.regime_label ?? 'Analyzing...',
    vpin: {
      level: raw.vpin?.level ?? 'normal',
      zScore: raw.vpin?.z_score ?? 0,
    },
    overall,
  };
}

/**
 * Raw backend trade/fill response (snake_case)
 */
export interface RawApiTrade {
  fill_id: string;
  order_id: string;
  symbol: string;
  side: string;
  /** Position action context: open, add, reduce, close */
  action?: string;
  size: number;
  price: number;
  fee: number;
  fee_currency?: string;
  timestamp: string;
  venue?: string;
}

/**
 * Transformed frontend trade (camelCase)
 */
export interface TransformedTrade {
  id: string;
  orderId: string;
  symbol: string;
  side: string;
  /** Position action context: open, add, reduce, close */
  action?: string;
  quantity: number;
  price: number;
  fee: number;
  feeCurrency: string;
  executedAt: string;
  venue: string;
}

/**
 * Transform a trade/fill response from backend format to frontend format
 */
export function transformTrade(raw: RawApiTrade): TransformedTrade {
  return {
    id: raw.fill_id,
    orderId: raw.order_id,
    symbol: raw.symbol,
    side: raw.side,
    action: raw.action || undefined,
    quantity: raw.size,
    price: raw.price,
    fee: raw.fee,
    feeCurrency: raw.fee_currency ?? '',
    executedAt: raw.timestamp,
    venue: raw.venue ?? 'unknown',
  };
}

/**
 * Transform an array of trades
 */
export function transformTrades(trades: RawApiTrade[]): TransformedTrade[] {
  return trades.map(transformTrade);
}

/**
 * Raw backend signal response (snake_case)
 */
export interface RawApiSignal {
  signal_id: string;
  symbol: string;
  signal_type: string;
  direction: string;
  confidence: number;
  price: number;
  strategy?: string;
  timeframe?: string;
  timestamp: string;
  expires_at?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Transformed frontend signal (camelCase)
 */
export interface TransformedSignal {
  id: string;
  symbol: string;
  type: string;
  action: string;
  confidence: number;
  price: number;
  strategy: string;
  timeframe: string;
  createdAt: string;
  expiresAt: string;
  metadata: Record<string, unknown>;
}

/**
 * Transform a signal response from backend format to frontend format
 */
export function transformSignal(raw: RawApiSignal): TransformedSignal {
  return {
    id: raw.signal_id,
    symbol: raw.symbol,
    type: raw.signal_type,
    action: raw.direction === 'long' ? 'buy' : 'sell',
    confidence: raw.confidence,
    price: raw.price,
    strategy: raw.strategy ?? 'unknown',
    timeframe: raw.timeframe ?? '1h',
    createdAt: raw.timestamp,
    expiresAt: raw.expires_at ?? '',
    metadata: raw.metadata ?? {},
  };
}

/**
 * Transform an array of signals
 */
export function transformSignals(signals: RawApiSignal[]): TransformedSignal[] {
  return signals.map(transformSignal);
}

// =============================================================================
// Kill Switch Response Transformer
// =============================================================================

/**
 * Raw backend kill switch status response
 * 
 * GAP-KILLSWITCH-002: Backend returns {kill_switch: "on"|"off"}
 */
export interface RawApiKillSwitchStatus {
  kill_switch: 'on' | 'off';
  service?: string;
  timestamp?: string;
}

/**
 * Raw backend kill switch action response
 */
export interface RawApiKillSwitchAction {
  ok: boolean;
  message?: string;
  timestamp?: string;
}

/**
 * Transformed frontend kill switch status
 * 
 * GAP-KILLSWITCH-002: Frontend expects {active: boolean}
 */
export interface TransformedKillSwitchStatus {
  active: boolean;
  status: 'active' | 'inactive';
  timestamp: string;
}

/**
 * Transform kill switch status from backend format to frontend format
 * 
 * GAP-KILLSWITCH-002: Response format mismatch
 * Backend: {kill_switch: "on"|"off"}
 * Frontend: {active: boolean}
 */
export function transformKillSwitchStatus(raw: RawApiKillSwitchStatus): TransformedKillSwitchStatus {
  return {
    active: raw.kill_switch === 'on',
    status: raw.kill_switch === 'on' ? 'active' : 'inactive',
    timestamp: raw.timestamp ?? new Date().toISOString(),
  };
}

/**
 * Transform kill switch action response
 */
export function transformKillSwitchAction(raw: RawApiKillSwitchAction, isActive: boolean): TransformedKillSwitchStatus {
  return {
    active: isActive,
    status: isActive ? 'active' : 'inactive',
    timestamp: raw.timestamp ?? new Date().toISOString(),
  };
}

// =============================================================================
// Proposal/Intent Response Transformer
// =============================================================================

/**
 * Raw backend proposal/intent response (snake_case)
 * 
 * GAP-PROPOSALS-001: Backend uses "intents" terminology
 */
export interface RawApiProposal {
  intent_id: string;
  symbol: string;
  side: string;
  order_type?: string;
  size: number;
  price?: number;
  state: string;
  created_at: string;
  updated_at?: string;
  expires_at?: string;
  projected_pnl?: number;
  risk_score?: number;
  warnings?: string[];
  rationale?: string;
}

/**
 * Transformed frontend proposal (camelCase)
 * 
 * GAP-PROPOSALS-001: Frontend uses "proposals" terminology
 */
export interface TransformedProposal {
  id: string;
  order: {
    symbol: string;
    side: string;
    type: string;
    quantity: number;
    price: number | undefined;
  };
  status: string;
  createdAt: string;
  expiresAt: string;
  riskAssessment: {
    projectedPnL: number;
    riskScore: number;
    warnings: string[];
  };
  rationale: string;
}

/**
 * Transform a proposal/intent response from backend format to frontend format
 * 
 * GAP-PROPOSALS-001: Naming mismatch (proposals vs intents)
 */
export function transformProposal(raw: RawApiProposal): TransformedProposal {
  return {
    id: raw.intent_id,
    order: {
      symbol: raw.symbol,
      side: raw.side,
      type: raw.order_type ?? 'market',
      quantity: raw.size,
      price: raw.price,
    },
    status: raw.state.toLowerCase(),
    createdAt: raw.created_at,
    expiresAt: raw.expires_at ?? '',
    riskAssessment: {
      projectedPnL: raw.projected_pnl ?? 0,
      riskScore: raw.risk_score ?? 0.5,
      warnings: raw.warnings ?? [],
    },
    rationale: raw.rationale ?? '',
  };
}

/**
 * Transform an array of proposals
 */
export function transformProposals(proposals: RawApiProposal[]): TransformedProposal[] {
  return proposals.map(transformProposal);
}

// =============================================================================
// Error Response Transformer
// =============================================================================

/**
 * Standardized backend error response
 * 
 * @see services/tradeclaw/src/tradeclaw/api.py - ErrorResponse class
 */
export interface RawApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  request_id?: string;
}

/**
 * Transformed frontend error
 */
export interface TransformedApiError {
  code: string;
  message: string;
  details: Record<string, unknown>;
  requestId: string;
  status: number;
}

/**
 * Transform an error response from backend format
 */
export function transformError(raw: RawApiError, status: number): TransformedApiError {
  return {
    code: raw.error?.code ?? 'unknown_error',
    message: raw.error?.message ?? 'An unknown error occurred',
    details: raw.error?.details ?? {},
    requestId: raw.request_id ?? '',
    status,
  };
}

/**
 * Get a user-friendly error message from an API error
 */
export function getUserFriendlyErrorMessage(error: TransformedApiError): string {
  const { code, message, details } = error;

  // Map error codes to user-friendly messages
  const errorMessages: Record<string, string> = {
    'not_found': 'The requested resource was not found.',
    'validation_error': 'The request data is invalid. Please check your input.',
    'unauthorized': 'You are not authorized to perform this action.',
    'forbidden': 'Access to this resource is forbidden.',
    'conflict': 'The request conflicts with the current state of the resource.',
    'kill_switch_active': 'Trading is currently disabled. The kill switch is active.',
    'mode_not_allowed': 'This action is not allowed in the current trading mode.',
    'intent_generation_failed': 'Failed to generate a trade proposal. Please try again.',
    'approval_failed': 'Failed to approve the trade proposal.',
    'rejection_failed': 'Failed to reject the trade proposal.',
  };

  // Check for specific error codes
  if (errorMessages[code]) {
    return errorMessages[code];
  }

  // Check for specific details
  if (details?.current_state) {
    return `Cannot perform this action. Current state: ${details.current_state}`;
  }

  if (details?.intent_id) {
    return `${message} (ID: ${details.intent_id})`;
  }

  // Fall back to the original message
  return message;
}